import {
  DiscountBxgy,
  DiscountFixedAmount,
  DiscountPercentage,
} from '@/modules/store/models/Discount';
import { CalculatedPrice } from '@/modules/store/models/Price';
import { formatPrice } from '@/utils/currency';

interface DiscountStarProps {
  price?: CalculatedPrice;
}

const discountStar = (
  <svg xmlns="http://www.w3.org/2000/svg" version="1.0" viewBox="0 0 1560 1560.41">
    <path
      d="M725.488 67.298c-28.68 35.584-56.52 69.609-61.867 75.611l-9.721 10.913-70.2-38.149c-38.61-20.982-77.479-42.096-86.376-46.921l-16.176-8.772-3.629 12.26c-1.996 6.743-14.517 49.466-27.825 94.941l-24.195 82.681-13.2-1.472c-87.291-9.734-181.98-19.376-182.747-18.609-.773.773 13.09 138.801 18.638 185.569.721 6.078.894 11.05.384 11.05-.773 0-169.914 49.175-184.322 53.589-5.21 1.596-9.711-7.534 54.74 111.047 19.292 35.495 34.048 65.438 32.792 66.54-1.256 1.102-35.336 28.607-75.734 61.123C35.653 751.214 2.6 778.834 2.6 780.075c0 1.242 33.052 28.809 73.45 61.26 40.398 32.451 74.477 59.921 75.732 61.045 1.255 1.124-18.927 40.934-44.849 88.467-25.921 47.533-46.737 86.816-46.258 87.295.48.479 42.779 13.139 93.999 28.132 51.221 14.994 93.509 27.643 93.976 28.109.466.466-3.885 44.475-9.669 97.798-5.785 53.322-10.075 97.392-9.534 97.933.541.541 44.611-3.749 97.934-9.533 53.322-5.785 97.35-10.117 97.839-9.628.489.49 12.653 41.223 27.031 90.519 14.378 49.295 27.021 91.601 28.096 94.012 1.661 3.729 14.375-2.421 85.153-41.187 45.76-25.064 84.719-46.195 86.575-46.96 1.856-.764 4.781.416 6.5 2.622 26.285 33.74 119.72 147.441 121.161 147.441 1.07 0 29.269-33.93 62.664-75.4 33.395-41.47 61.609-75.4 62.696-75.4 1.087 0 29.253 14.99 62.591 33.312 89.394 49.129 111.313 60.797 111.313 59.258 0-2.821 54.161-186.597 55.34-187.776.673-.674 44.871 3.489 98.217 9.249 53.346 5.761 97.326 10.141 97.733 9.734.407-.407-3.99-44.37-9.772-97.695-5.782-53.325-9.967-97.5-9.3-98.167.667-.667 43.062-13.421 94.209-28.342 51.148-14.922 93.341-27.474 93.761-27.894.42-.421-20.24-39.101-45.912-85.957-25.672-46.856-46.676-86.049-46.676-87.096 0-1.046 33.93-29.198 75.4-62.558 41.47-33.36 75.4-61.559 75.4-62.663 0-1.104-32.467-28.057-72.15-59.896-39.683-31.839-73.775-59.38-75.76-61.202-3.56-3.267 13.887-37.819 76.049-150.607 13.972-25.352 14.188-26.052 8.65-28.105-3.123-1.157-45.322-13.693-93.774-27.856-48.452-14.164-88.591-26.248-89.197-26.854-.606-.606 3.593-44.767 9.331-98.136 5.739-53.368 10.108-97.359 9.71-97.757-.399-.398-44.407 3.953-97.796 9.671-53.39 5.717-97.57 9.897-98.178 9.288-.609-.608-13.396-43.033-28.416-94.278-15.02-51.245-27.544-93.41-27.832-93.699-.288-.29-27.391 14.13-60.23 32.044-121.795 66.441-113.113 62.3-117.903 56.239C862.725 101.816 781.138 2.6 779.633 2.6c-1.099 0-25.464 29.114-54.145 64.698"
      fillRule="evenodd"
      fill="red"
      stroke="white"
      strokeWidth={25}
    />
  </svg>
);

const DiscountStar = ({ price }: DiscountStarProps) => {
  if (!price || !price.promotion) return null;

  const discountPercentage: DiscountPercentage | undefined =
    price.discountPercentage ??
    price.promotion?.discounts.find(
      (d): d is DiscountPercentage => d.discountType === 'percentage',
    );

  if (discountPercentage) {
    return (
      <div className="absolute top-0 right-0 w-16 h-16">
        <span className="text-white text-center absolute w-full py-3 leading-tight font-semibold">
          {discountPercentage.percentage}%<br />
          off
        </span>
        {discountStar}
      </div>
    );
  }

  const discountFixedAmount: DiscountFixedAmount | undefined =
    price.discountFixedAmount ??
    price.promotion?.discounts.find(
      (d): d is DiscountFixedAmount => d.discountType === 'fixedAmount',
    );

  if (discountFixedAmount) {
    return (
      <div className="absolute top-0 right-0 w-16 h-16">
        <span className="text-white text-center absolute w-full py-4 leading-tight tracking-tighter font-semibold text-sm">
          {formatPrice(discountFixedAmount.amount)}
          <br />
          off
        </span>
        {discountStar}
      </div>
    );
  }

  const bxgyDiscount: DiscountBxgy | undefined =
    price.discountBxgy ??
    price.promotion?.discounts.find((d): d is DiscountBxgy => d.discountType === 'bxgy');

  if (bxgyDiscount) {
    return (
      <div className="absolute top-0 right-0 w-16 h-16">
        <span className="text-white text-center absolute w-full py-3 leading-tight font-semibold">
          Buy {bxgyDiscount.buyQuantity} Get {bxgyDiscount.getQuantity}
        </span>
        {discountStar}
      </div>
    );
  }
};

export default DiscountStar;
